/**
 * useBiometricLogin - Face ID / Touch ID state and sign-in for the login flow
 *
 * Extracted from useLogin.js. Owns:
 * - Biometric hardware/enrollment availability
 * - Secure credential storage (save/read) and the enable-Face-ID prompt
 * - Biometric sign-in, including the auto-attempt on app open
 *
 * The parent hook owns the email/password form state and post-sign-in setup
 * (executeLogin), so those are injected. tryBiometricLogin and
 * attemptAutoBiometricLogin take the parent's executeLogin as an argument
 * because the parent builds it from this hook's own maybePromptEnableBiometrics.
 *
 * @param {Object} navigation - React Navigation object (for ResetPassword redirect)
 * @param {Object} deps
 * @param {Function} deps.handleError - Parent error handler
 * @param {Function} deps.setEmail - Parent form setter
 * @param {Function} deps.setPassword - Parent form setter
 * @param {Function} deps.setIsBusy - Parent loading setter
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Alert } from 'react-native';
import { signIn } from 'aws-amplify/auth';
import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';

import { isWeb } from '../util/platform';
import { getString, setString } from '../util/storage';
import { showSuccess } from '../util/error';
import { t } from '../i18n';

const CREDS_KEY = 'biometricCreds_v1';
const BIOMETRICS_ENABLED_KEY = 'biometricsEnabled_v1';

export default function useBiometricLogin(navigation, { handleError, setEmail, setPassword, setIsBusy }) {
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [hasStoredCreds, setHasStoredCreds] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const bioAutoAttemptedRef = useRef(false);

  /**
   * Save credentials for biometric login
   */
  const saveCredsForBiometrics = useCallback(async (emailToSave, passwordToSave) => {
    if (isWeb()) return;

    try {
      await SecureStore.setItemAsync(
        CREDS_KEY,
        JSON.stringify({ email: emailToSave, password: passwordToSave }),
        {
          requireAuthentication: true,
          keychainService: 'loginCreds',
          accessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
        },
      );
      await setString(BIOMETRICS_ENABLED_KEY, '1');
      setHasStoredCreds(true);
      showSuccess(t('auth:biometric.enabled'));
    } catch (e) {
      handleError(e, { userMessage: t('auth:biometric.enableFailed') });
    }
  }, [handleError]);

  /**
   * Read credentials from SecureStore for biometric login
   */
  const readCredsForBiometrics = useCallback(async () => {
    if (isWeb()) return null;

    try {
      const raw = await SecureStore.getItemAsync(CREDS_KEY, {
        requireAuthentication: true,
        keychainService: 'loginCreds',
      });
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Offer to enable Face ID after a successful password login
   */
  const maybePromptEnableBiometrics = useCallback((email, password) => {
    if (biometricAvailable && email && password && !hasStoredCreds && !isWeb()) {
      Alert.alert(t('auth:biometric.enablePromptTitle'), t('auth:biometric.enablePromptMessage'), [
        { text: t('auth:biometric.notNow') },
        {
          text: t('auth:biometric.enable'),
          onPress: async () => {
            await saveCredsForBiometrics(email, password);
          },
        },
      ]);
    }
  }, [biometricAvailable, hasStoredCreds, saveCredsForBiometrics]);

  /**
   * Attempt biometric login
   * @param {Function} executeLogin - Parent post-sign-in setup/navigation
   */
  const tryBiometricLogin = useCallback(async (executeLogin) => {
    if (isWeb()) return;

    if (!biometricAvailable) {
      Alert.alert(t('auth:biometric.unavailableTitle'), t('auth:biometric.unavailableMessage'));
      return;
    }

    setBioBusy(true);
    try {
      const res = await LocalAuthentication.authenticateAsync({
        promptMessage: t('auth:biometric.logInWith'),
        cancelLabel: t('auth:biometric.usePassword'),
        disableDeviceFallback: false,
      });

      if (!res.success) {
        setBioBusy(false);
        return;
      }

      const stored = await readCredsForBiometrics();
      if (!stored) {
        Alert.alert(t('auth:biometric.notSetUpTitle'), t('auth:biometric.notSetUpMessage'));
        setBioBusy(false);
        return;
      }

      setEmail(stored.email);
      setPassword(stored.password);

      const user = await signIn({
        username: stored.email,
        password: stored.password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });

      if (user.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        navigation.navigate('ResetPassword', {
          initialLogin: true,
          user,
          authenticatedEmail: stored.email,
          oldPassword: stored.password,
        });
      } else {
        await executeLogin();
      }
    } catch (e) {
      handleError(e, { userMessage: t('auth:biometric.signInFailed') });
      setIsBusy(false);
    } finally {
      setBioBusy(false);
    }
  }, [biometricAvailable, readCredsForBiometrics, navigation, setEmail, setPassword, setIsBusy, handleError]);

  /**
   * Auto-attempt biometric login on app open (once), if enabled on this device
   * @param {Function} executeLogin - Parent post-sign-in setup/navigation
   */
  const attemptAutoBiometricLogin = useCallback(async (executeLogin) => {
    if (isWeb()) return;
    if (bioAutoAttemptedRef.current) return;

    let available = biometricAvailable;
    if (!available) {
      try {
        const hasHardware = await LocalAuthentication.hasHardwareAsync();
        const isEnrolled = hasHardware ? await LocalAuthentication.isEnrolledAsync() : false;
        available = Boolean(hasHardware && isEnrolled);
        setBiometricAvailable(available);
      } catch {
        available = false;
      }
    }

    const enabledFlag =
      hasStoredCreds || (await getString(BIOMETRICS_ENABLED_KEY)) === '1';
    if (available && enabledFlag && !bioBusy) {
      bioAutoAttemptedRef.current = true;
      await tryBiometricLogin(executeLogin);
    }
  }, [biometricAvailable, hasStoredCreds, bioBusy, tryBiometricLogin]);

  // Check biometric availability on mount
  useEffect(() => {
    (async () => {
      if (isWeb()) return;

      try {
        const hasHardware = await LocalAuthentication.hasHardwareAsync();
        const isEnrolled = hasHardware ? await LocalAuthentication.isEnrolledAsync() : false;
        setBiometricAvailable(Boolean(hasHardware && isEnrolled));

        const enabled = await getString(BIOMETRICS_ENABLED_KEY);
        setHasStoredCreds(enabled === '1');
      } catch {
        setBiometricAvailable(false);
        setHasStoredCreds(false);
      }
    })();
  }, []);

  return {
    biometricAvailable,
    hasStoredCreds,
    bioBusy,
    saveCredsForBiometrics,
    maybePromptEnableBiometrics,
    tryBiometricLogin,
    attemptAutoBiometricLogin,
  };
}

export { CREDS_KEY, BIOMETRICS_ENABLED_KEY };
